/**
 * Integration Monitoring Dashboard
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  Webhook as WebhookIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { IntegrationStats, IntegrationLog, WebhookConfig, ScheduledImport } from '../../types/integration';

const IntegrationMonitoring: React.FC = () => {
  const queryClient = useQueryClient();
  const [currentTab, setCurrentTab] = useState(0);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [scheduledImportDialogOpen, setScheduledImportDialogOpen] = useState(false);

  const [webhookForm, setWebhookForm] = useState({
    name: '',
    url: '',
    event_type: 'product_update',
  });

  const [scheduledImportForm, setScheduledImportForm] = useState({
    name: '',
    data_type: 'products',
    source_type: 'http',
    source_config: {},
    schedule_cron: '0 8 * * *',
  });

  // Fetch stats
  const { data: stats, isLoading: loadingStats } = useQuery<IntegrationStats>({
    queryKey: ['integrationStats'],
    queryFn: async () => {
      const response = await api.getIntegrationStats();
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30s
  });

  // Fetch logs
  const { data: logs } = useQuery<IntegrationLog[]>({
    queryKey: ['integrationLogs'],
    queryFn: async () => {
      const response = await api.getIntegrationLogs(100);
      return response.data;
    },
    refetchInterval: 10000,
  });

  // Fetch webhooks
  const { data: webhooks } = useQuery<WebhookConfig[]>({
    queryKey: ['webhooks'],
    queryFn: async () => {
      const response = await api.getWebhooks();
      return response.data;
    },
  });

  // Fetch scheduled imports
  const { data: scheduledImports } = useQuery<ScheduledImport[]>({
    queryKey: ['scheduledImports'],
    queryFn: async () => {
      const response = await api.getScheduledImports();
      return response.data;
    },
  });

  // Create webhook mutation
  const createWebhookMutation = useMutation({
    mutationFn: (data: any) => api.createWebhook(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      setWebhookDialogOpen(false);
      setWebhookForm({ name: '', url: '', event_type: 'product_update' });
    },
  });

  // Delete webhook mutation
  const deleteWebhookMutation = useMutation({
    mutationFn: (id: string) => api.deleteWebhook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });

  // Toggle webhook mutation
  const toggleWebhookMutation = useMutation({
    mutationFn: (id: string) => api.toggleWebhook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });

  // Create scheduled import mutation
  const createScheduledImportMutation = useMutation({
    mutationFn: (data: any) => api.createScheduledImport(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledImports'] });
      setScheduledImportDialogOpen(false);
    },
  });

  // Delete scheduled import mutation
  const deleteScheduledImportMutation = useMutation({
    mutationFn: (id: string) => api.deleteScheduledImport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledImports'] });
    },
  });

  const handleCreateWebhook = () => {
    createWebhookMutation.mutate(webhookForm);
  };

  const handleCreateScheduledImport = () => {
    createScheduledImportMutation.mutate(scheduledImportForm);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <DashboardIcon sx={{ fontSize: 40, mr: 2 }} />
        <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>Integration Monitoring</Typography>
      </Box>

      {/* Stats Cards */}
      {loadingStats ? (
        <CircularProgress />
      ) : stats ? (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Total Imports
                    </Typography>
                    <Typography variant="h4">{stats.total_imports}</Typography>
                  </Box>
                  <UploadIcon color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Total Exports
                    </Typography>
                    <Typography variant="h4">{stats.total_exports}</Typography>
                  </Box>
                  <DownloadIcon color="info" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Active Webhooks
                    </Typography>
                    <Typography variant="h4">
                      {stats.active_webhooks}/{stats.total_webhooks}
                    </Typography>
                  </Box>
                  <WebhookIcon color="success" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      Health Status
                    </Typography>
                    <Chip
                      label={stats.health_status}
                      color={stats.health_status === 'healthy' ? 'success' : 'warning'}
                    />
                  </Box>
                  {stats.health_status === 'healthy' ? (
                    <SuccessIcon color="success" sx={{ fontSize: 40 }} />
                  ) : (
                    <ErrorIcon color="warning" sx={{ fontSize: 40 }} />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(_, val) => setCurrentTab(val)}>
          <Tab label="Activity Logs" />
          <Tab label="Webhooks" />
          <Tab label="Scheduled Imports" />
        </Tabs>
      </Paper>

      {/* Activity Logs Tab */}
      {currentTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Timestamp</strong></TableCell>
                  <TableCell><strong>Operation</strong></TableCell>
                  <TableCell><strong>Sync Status</strong></TableCell>
                  <TableCell><strong>Details</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs && logs.length > 0 ? (
                  logs.map((log) => (
                    <TableRow key={log.log_id} hover>
                      <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                      <TableCell>{log.operation}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          color={log.status === 'COMPLETED' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{log.details}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No activity logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Webhooks Tab */}
      {currentTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Webhooks</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setWebhookDialogOpen(true)}
            >
              Add Webhook
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>URL</strong></TableCell>
                  <TableCell><strong>Event Type</strong></TableCell>
                  <TableCell><strong>Webhook Status</strong></TableCell>
                  <TableCell><strong>Last Triggered</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {webhooks && webhooks.length > 0 ? (
                  webhooks.map((webhook) => (
                    <TableRow key={webhook.webhook_id} hover>
                      <TableCell>{webhook.name}</TableCell>
                      <TableCell>{webhook.url}</TableCell>
                      <TableCell>{webhook.event_type}</TableCell>
                      <TableCell>
                        <Chip
                          label={webhook.is_active ? 'Active' : 'Inactive'}
                          color={webhook.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {webhook.last_triggered
                          ? new Date(webhook.last_triggered).toLocaleString()
                          : 'Never'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => toggleWebhookMutation.mutate(webhook.webhook_id)}
                            color={webhook.is_active ? 'success' : 'default'}
                          >
                            {webhook.is_active ? <ToggleOnIcon /> : <ToggleOffIcon />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => deleteWebhookMutation.mutate(webhook.webhook_id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No webhooks configured
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Scheduled Imports Tab */}
      {currentTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Scheduled Imports</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setScheduledImportDialogOpen(true)}
            >
              Add Scheduled Import
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Data Type</strong></TableCell>
                  <TableCell><strong>Source</strong></TableCell>
                  <TableCell><strong>Schedule</strong></TableCell>
                  <TableCell><strong>Last Run</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scheduledImports && scheduledImports.length > 0 ? (
                  scheduledImports.map((scheduled) => (
                    <TableRow key={scheduled.schedule_id} hover>
                      <TableCell>{scheduled.name}</TableCell>
                      <TableCell>{scheduled.data_type}</TableCell>
                      <TableCell>{scheduled.source_type}</TableCell>
                      <TableCell>{scheduled.schedule_cron}</TableCell>
                      <TableCell>
                        {scheduled.last_run ? new Date(scheduled.last_run).toLocaleString() : 'Never'}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => deleteScheduledImportMutation.mutate(scheduled.schedule_id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No scheduled imports configured
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Webhook Dialog */}
      <Dialog open={webhookDialogOpen} onClose={() => setWebhookDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Webhook</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={webhookForm.name}
              onChange={(e) => setWebhookForm({ ...webhookForm, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="URL"
              value={webhookForm.url}
              onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={webhookForm.event_type}
                label="Event Type"
                onChange={(e) => setWebhookForm({ ...webhookForm, event_type: e.target.value })}
              >
                <MenuItem value="product_update">Product Update</MenuItem>
                <MenuItem value="inventory_change">Inventory Change</MenuItem>
                <MenuItem value="sales_transaction">Sales Transaction</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWebhookDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateWebhook} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scheduled Import Dialog */}
      <Dialog open={scheduledImportDialogOpen} onClose={() => setScheduledImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Scheduled Import</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={scheduledImportForm.name}
              onChange={(e) => setScheduledImportForm({ ...scheduledImportForm, name: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Data Type</InputLabel>
              <Select
                value={scheduledImportForm.data_type}
                label="Data Type"
                onChange={(e) => setScheduledImportForm({ ...scheduledImportForm, data_type: e.target.value })}
              >
                <MenuItem value="products">Products</MenuItem>
                <MenuItem value="inventory">Inventory</MenuItem>
                <MenuItem value="sales">Sales</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Schedule (Cron)"
              value={scheduledImportForm.schedule_cron}
              onChange={(e) => setScheduledImportForm({ ...scheduledImportForm, schedule_cron: e.target.value })}
              placeholder="0 8 * * *"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduledImportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateScheduledImport} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntegrationMonitoring;
